import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorOptions,
	CreatorRendererOptions,
	CreatorSiphon,
	CreatorState,
} from '@/types/creator';
import { isDev } from '@/components/App';
import { ThreeEvent, ThreeEvents } from '@/renderer/types';
import {
	generateInitialSelectedValues,
	prepareAttributes,
	roundToHalf,
} from '@/utils/utilities';
import { Product, ProductVariation } from '@/api/ProductApi';
import { AttributeValue } from '@/api/AttributeValueApi';
import { UUID } from '@/types/types';

const initialState: CreatorState = {
	product: null,
	matchedVariation: null,
	variations: [],
	selectedAttributeValues: {},
	preparedAttributes: [],
	realizationTime: '',
	length: 0,
	siphons: [],
	renderer: {
		cameraDistance: 0,
		loading: false,
	},
	options: {
		isMetersVisible: false,
		isOpen: isDev,
		syncProjectCounter: 0,
		isAutoSiphonsPositioning: true,
		isLoading: false,
		areMetersVisible: false,
		helpDrawerOpened: false,
		helpDrawerData: null,
		maxLength: 0,
		maxSiphonsXpos: Infinity,
		minSiphonsXpos: 0,
	},
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		setProduct: (
			state,
			{
				payload,
			}: PayloadAction<{
				product: Product;
				variations: ProductVariation[];
			}>
		) => {
			state.product = payload.product;
			state.variations = payload.variations;
			state.selectedAttributeValues = {};
			state.matchedVariation = null;
			state.options.maxLength = 0;
			state.length = 0;
			state.siphons = [];

			state.preparedAttributes = prepareAttributes(payload.product);

			if (payload.product.defaultVariation) {
				state.selectedAttributeValues = generateInitialSelectedValues(
					payload.product.defaultVariation
				);
				state.matchedVariation = payload.product.defaultVariation;
				state.options.maxLength = payload.product.defaultVariation.maxLength;
			}
		},
		setVariation: (state, { payload }: PayloadAction<ProductVariation>) => {
			state.matchedVariation = payload;
			state.options.maxLength = payload.maxLength;
		},
		selectAttributeValue: (
			state,
			{ payload }: PayloadAction<{ attributeId: UUID; value: AttributeValue }>
		) => {
			state.selectedAttributeValues[payload.attributeId] = payload.value;
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		updateRenderer: (
			state,
			{ payload }: PayloadAction<Partial<CreatorRendererOptions>>
		) => {
			Object.assign(state.renderer, payload);
		},
		setLength: (state, { payload }: PayloadAction<number>) => {
			state.length = payload;
		},
		handleRendererEvent: (
			state,
			{
				payload,
			}: PayloadAction<{
				type: ThreeEvent;
				data: ThreeEvents[ThreeEvent];
			}>
		) => {
			const { type } = payload;

			switch (type) {
				case ThreeEvent.SET_LENGTH: {
					state.length = payload.data as number;
					break;
				}
				case ThreeEvent.UPDATE_CAMERA_DISTANCE: {
					state.renderer.cameraDistance = roundToHalf(payload.data as number);
					break;
				}
				case ThreeEvent.SET_OPTIONS: {
					Object.assign(state.options, payload.data);
					break;
				}
				case ThreeEvent.SET_SIPHON_POS_X: {
					const data = payload.data as ThreeEvents[ThreeEvent.SET_SIPHON_POS_X];
					for (const creatorSiphon of state.siphons) {
						if (creatorSiphon.id !== data.id) continue;
						creatorSiphon.posX = data.posX;
						break;
					}
					break;
				}
				default:
					throw new Error('UNKNOW_EVENT');
			}
		},
		toogleHelpDrawer: (
			state,
			{
				payload,
			}: PayloadAction<
				Pick<CreatorOptions, 'helpDrawerOpened' | 'helpDrawerData'>
			>
		) => {
			Object.assign(state.options, payload);
		},
		toogleInstructionModal: (
			state,
			{
				payload,
			}: PayloadAction<
				Pick<CreatorOptions, 'helpDrawerOpened' | 'helpDrawerData'>
			>
		) => {
			Object.assign(state.options, payload);
		},
		refreshAPIData: (state) => {
			state.options.syncProjectCounter++;
		},
		setSiphonsQuantity: (state, { payload }: PayloadAction<number>) => {
			const diff = payload - state.siphons.length;
			if (!diff) return;

			if (diff > 0)
				for (let i = 0; i < diff; i++) {
					state.siphons.push({
						id: crypto.randomUUID(),
						posX: state.length / 2,
					});
				}
			else
				for (let i = 0; i < -diff; i++) {
					state.siphons.pop();
				}
		},
		addCreatorSiphon: (state, { payload }: PayloadAction<CreatorSiphon>) => {
			state.siphons.push(payload);
		},
		removeCreatorSiphon: (state, { payload }: PayloadAction<UUID>) => {
			state.siphons = state.siphons.filter((siphon) => siphon.id !== payload);
		},
		setIsAutoSiphonsPositioning: (
			state,
			{ payload }: PayloadAction<boolean>
		) => {
			state.options.isAutoSiphonsPositioning = payload;
		},
		setSiphonPosX: (
			state,
			{ payload }: PayloadAction<{ id: UUID; posX: number }>
		) => {
			const siphon = state.siphons.find((s) => s.id === payload.id);
			if (siphon) siphon.posX = payload.posX;
		},
		resetCreator: (state) => {},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
